import type { Directive, DirectiveBinding } from 'vue';

import type { AnalyticsEvent, AnalyticsParams } from './events';
import type { Tracker } from './Tracker';
import { resolveTrigger, type Modifiers, type TrackTrigger } from './triggers';

export type TrackValue =
  | AnalyticsEvent
  | { event: AnalyticsEvent; params?: AnalyticsParams }
  | null
  | undefined;

class TrackBinding {
  private constructor(
    readonly event: AnalyticsEvent,
    readonly params: AnalyticsParams,
    readonly trigger: TrackTrigger,
  ) {}

  static from(
    value: TrackValue,
    modifiers: Modifiers = {},
  ): TrackBinding | null {
    if (!value) return null;

    const { event, params = {} } =
      typeof value === 'string' ? { event: value } : value;

    return new TrackBinding(event, params, resolveTrigger(modifiers));
  }
}

export class TrackRegistry {
  private readonly bindings = new WeakMap<Element, TrackBinding>();

  register(element: Element, binding: TrackBinding | null): void {
    if (binding) this.bindings.set(element, binding);
    else this.bindings.delete(element);
  }

  unregister(element: Element): void {
    this.bindings.delete(element);
  }

  resolve(
    target: EventTarget | null,
  ): { element: Element; binding: TrackBinding } | null {
    let element = target instanceof Element ? target : null;

    // The tracked element closest to the target wins, one event per click.
    while (element) {
      const binding = this.bindings.get(element);
      if (binding) return { element, binding };
      element = element.parentElement;
    }

    return null;
  }
}

export class ClickTrackingListener {
  private root: Document | null = null;

  constructor(
    private readonly registry: TrackRegistry,
    private readonly tracker: Tracker,
  ) {}

  attach(root: Document = document): void {
    this.detach();
    this.root = root;
    root.addEventListener('click', this.handleClick, { capture: true });
  }

  detach(): void {
    this.root?.removeEventListener('click', this.handleClick, {
      capture: true,
    });
    this.root = null;
  }

  private handleClick = (click: MouseEvent): void => {
    const tracked = this.registry.resolve(click.target);
    if (!tracked) return;

    const { element, binding } = tracked;
    if (binding.trigger.shouldFire(click, element)) {
      this.tracker.track(binding.event, binding.params);
    }
  };
}

export function createTrackDirective(
  registry: TrackRegistry,
): Directive<HTMLElement, TrackValue> {
  const register = (
    element: HTMLElement,
    { value, modifiers }: DirectiveBinding<TrackValue>,
  ) => registry.register(element, TrackBinding.from(value, modifiers));

  return {
    mounted: register,
    updated: register,
    beforeUnmount: element => registry.unregister(element),
  };
}
